import {useEffect, useState} from "react";
import {Col, Form, Row} from "react-bootstrap";
import {BiEdit, BiTrash} from "react-icons/bi";
import {Post} from "../../lib/Request";
import {ALERT_VARIANT, DefaultOffice, EResponse_code, OfficeActionType} from "../../const/dataEnum";
import {setAlert} from "../../stores/actions/alertModalStateAction";
import {useDispatch} from "react-redux";
import {API_ACTION, API_COMPANY} from "../../const/Api";
import {FmlxButton, FmlxIcon, FmlxModal, FmlxTable} from 'fmlx-common-ui';
import useOffices from "../../hooks/useOffice";
import './ManagementOffice.scss';

export default function ManageOffice() {
  const dispatch = useDispatch();
  const defaultOffice = DefaultOffice.DEFFAULT_VALUE;
  const {offices, loadingOffices, fetchOffices} = useOffices();
  const [formState, setFormState] = useState(defaultOffice);
  const [action, setAction] = useState("");
  const [open, setOpen] = useState(false);
  const [loadingModify, setLoadingModify] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  useEffect(() => {
    if (submissionSuccess) {
      fetchOffices();
      setSubmissionSuccess(false);
    }
  }, [submissionSuccess, fetchOffices]);

  const modifyOffice = async (event) => {
    if (event) event.preventDefault();
    setLoadingModify(true);

    const param = {...formState};

    let url;
    switch (action) {
      case OfficeActionType.CREATE:
        url = API_COMPANY + API_ACTION.CREATE;
        break;
      case OfficeActionType.UPDATE:
        url = API_COMPANY + API_ACTION.UPDATE;
        break;
      case OfficeActionType.DELETE:
        url = API_COMPANY + API_ACTION.DELETE;
        break;
      default:
        setLoadingModify(false);
        return;
    }

    try {
      const result = await Post(url, param);
      if (result.responseCode === EResponse_code.SUCCESS) {
        dispatch(setAlert({show: true, variant: ALERT_VARIANT.SUCCESS, message: `${result.message}`}));
        setSubmissionSuccess(true);
      } else {
        dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: result.message}));
      }
    } catch (error) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: "An error occurred"}));
    } finally {
      resetForm();
      setLoadingModify(false);
      handleOnClose();
    }
  };

  const resetForm = () => {
    setAction("");
    setFormState(defaultOffice);
    setLoadingModify(false);
  };

  const handleOfficeSelection = (selectedItems) => {
    if (selectedItems && selectedItems.length > 0) {
      setFormState({
        id: selectedItems[0].id,
        companyName: selectedItems[0]?.companyName || '',
        address: selectedItems[0]?.address || ''
      });
    }
  };

  const handleOnClickOpen = () => {
    setOpen(true);
  };

  const handleOnClose = () => {
    resetForm();
    setOpen(false);
  };

  const handleOnClickRegister = () => {
    setAction(OfficeActionType.CREATE);
    handleOnClickOpen();
  }

  const handleOnClickEdit = (row) => {
    setAction(OfficeActionType.UPDATE);
    setFormState(row.original);
    handleOnClickOpen();
  }

  const handleOnClickDelete = (row) => {
    setAction(OfficeActionType.DELETE);
    setFormState(row.original);
    handleOnClickOpen();
  }

  const handleRenderModalBody = () => (
    action === OfficeActionType.DELETE ? "delete this office?" : (
      <Form>
        <Row g-3>
          <Col>
            <Form.Group size="lg" controlId="companyName" className="mb-2">
              <Form.Label>Company</Form.Label>
              <Form.Control
                autoFocus
                placeholder="Company"
                type="text"
                value={formState.companyName}
                onChange={(e) => setFormState({...formState, companyName: e.target.value})}
              />
            </Form.Group>
            <Form.Group size="lg" controlId="address" className="mb-2">
              <Form.Label>Address</Form.Label>
              <Form.Control
                placeholder="Address"
                type="text"
                value={formState.address}
                onChange={(e) => setFormState({...formState, address: e.target.value})}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>
    )
  );

  return (
    <div className="manage-office-container">
      <div className="manage-office">
        <div className="manage-office--button-container">
          <FmlxButton
            label="Register New Office"
            type="basic"
            onClick={handleOnClickRegister}
          />
        </div>
        <div className="manage-office--table-container">
          <FmlxTable
            data={offices}
            getSelectedRow={handleOfficeSelection}
            isLoading={loadingOffices}
            columns={[
              {
                Header: "No",
                accessor: null,
                editable: false,
                disableSort: true,
                hideSort: true,
                hideFilter: true,
                headerStyle: {
                  width: '10%',
                  minWidth: '10%',
                  maxWidth: '10%',
                },
                Cell: ({row}) => <>{row.index + 1}</>
              },
              {
                Header: "Company",
                accessor: 'companyName',
                editable: false,
                disableSort: true,
                hideSort: true,
                hideFilter: true,
                headerStyle: {
                  width: '40%',
                  minWidth: '40%',
                  maxWidth: '40%',
                },
              },
              {
                Header: "Address",
                accessor: 'address',
                editable: false,
                disableSort: true,
                hideSort: true,
                hideFilter: true,
                headerStyle: {
                  width: '40%',
                  minWidth: '40%',
                  maxWidth: '40%',
                },
              },
              {
                Header: "Action",
                accessor: null,
                editable: false,
                disableSort: true,
                hideSort: true,
                hideFilter: true,
                headerStyle: {
                  width: '10%',
                  minWidth: '10%',
                  maxWidth: '10%',
                },
                Cell: ({row}) => (
                  <div style={{display: "flex", gap: "8px"}}>
                    <FmlxButton
                      label={<BiEdit/>}
                      type="primary"
                      onClick={() => handleOnClickEdit(row)}
                    />
                    <FmlxButton
                      label={<BiTrash/>}
                      type="danger"
                      onClick={() => handleOnClickDelete(row)}
                    />
                  </div>
                ),
              }
            ]}
          />
        </div>
      </div>

      <FmlxModal
        open={open}
        size="lg"
        title={`${action} Office`}
        onCloseClick={handleOnClose}
        primaryButton={{
          label: loadingModify ? (
            <div>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </div>
          ) : (
            action === OfficeActionType.DELETE ? "Yes" : "Save Changes"
          ),
          onClick: modifyOffice,
          disabled: loadingModify || (action !== OfficeActionType.DELETE && !formState.companyName),
          variant: action === OfficeActionType.DELETE ? "danger" : "primary",
        }}
        secondaryButton={{
          label: 'Cancel',
          onClick: {handleOnClose},
          disabled: loadingModify,
          variant: 'secondary',
        }}
        customHeader={
          <div style={{
            padding: '1rem 1.50rem 1rem 1.50rem',
            display: 'flex',
            alignItems: 'center',
            fontFamily: '"Roboto", sans-serif',
            fontSize: '1.25rem',
            lineHeight: '120%',
            fontWeight: '400',
          }}>
            <strong>{`${action} Office`}</strong>
            <div
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto',
                paddingLeft: '1rem',
              }}
              role="button"
              onClick={handleOnClose}
            >
              <FmlxIcon
                name="CloseOutline"
                size={FmlxIcon.Size.MEDIUM}
              />
            </div>
          </div>
        }
        customFooter={
          <div style={{
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderTop: '0.06rem solid #cacaca',
          }}>
            <FmlxButton
              label={action === OfficeActionType.DELETE ? "Yes" : "Save"}
              type={action === OfficeActionType.DELETE ? "danger" : "basic"}
              onClick={() => {
                modifyOffice();
              }}
              disabled={loadingModify || (action !== OfficeActionType.DELETE && !formState.companyName)}
            />
          </div>
        }
      >
        {handleRenderModalBody()}
      </FmlxModal>
    </div>
  );
}
